import { Swiper, SwiperSlide } from "swiper/react";
import { SlideBulletNavigation } from "../../../../../components/blog/RelatedPosts/SlideBulletNavigation";
import { Line } from "../../components/Line";
import { Subtitle } from "../../components/Subtitle";
import { Text } from "../../components/Text";
import "swiper/css";
import styled from "styled-components";
import { device, size } from "../../../../../constants";
import { useState } from "react";
import { SlideArrowNavigation } from "../../../../../components/blog/RelatedPosts/SlideArrowNavigation";
import { cn } from "../../../../../lib/utils";
import { useTranslation } from "next-i18next";

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
	.arrows {
		display: none;

		button {
			&, :is([data-theme="dark"] &) {
				background-color: #2B0C1A;
				border-color: #2B0C1A;
			}

			svg path {
				color: #FFCC32;

				:is([data-theme="dark"] &) {
					color: #FFCC32;
				}
			}
		}

		button:disabled {
			&, :is([data-theme="dark"] &) {
				background-color: transparent;
				border-color: #2B0C1A;
			}

			svg path {
				&, :is([data-theme="dark"] &) {
					color: #2B0C1A;
				}
			}
		}
	}
	
  @media ${device.laptop} {
		justify-content: space-between;
    align-items: flex-start;
		
		.bullets {
			display: none;
		}

    .arrows {
      display: block;
			margin-left: auto;
    }
  }
`;

const SwiperWrapper = styled.div`
  .bullets-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bullet {
    background-color: #2B0C1A;
    opacity: 0.5;
  }
  
  .bullet-active {
    opacity: 1;
  }

  :is([data-theme="dark"] &) {
    .bullet {
      background-color: #2B0C1A;
    }
  }

	.swiper-slide:last-child > div {
		border-right: none;
	}
`;

function ResultCard({ result }) {
	return (
		<div
			className={cn(
				"px-4 py-4 flex flex-col justify-center items-center mx-auto border-r-0 border-r-[#2B0C1A]",
				[
					"laptop:px-6 laptop:pl-0 laptop:max-w-none laptop:mx-0 laptop:border-r-[3px]",
				],
			)}
		>
			<span
				className={cn(
					"max-w-[310px] font-bold h-[64px] text-[72px] text-[#2B0C1A] inline-flex items-center justify-center",
					["laptop:max-w-[272px] laptop:text-[70px] laptop:h-[80px]"],
				)}
			>
				{result.number}
			</span>
			<span
				className={cn(
					"max-w-[310px] text-[32px] font-bold text-[#2B0C1A] mt-4 h-[48px] inline-flex items-center justify-center",
					["laptop:max-w-[272px] laptop:mt-2"],
				)}
			>
				{result.description}
			</span>
			<p
				className={cn(
					"max-w-[310px] text-base leading-7 text-[#2B0C1A] mt-4 text-center",
					["laptop:max-w-[272px] laptop:text-lg"],
				)}
			>
				{result.content}
			</p>
		</div>
	);
}

export default function ResultsSection() {
	const { t } = useTranslation();
	const [activeBullet, setActiveBullet] = useState(0);

	const results = [
		{
			number: t("products.grupoAvenida.results.metrics.productivity.number"),
			description: t(
				"products.grupoAvenida.results.metrics.productivity.description",
			),
			content: t("products.grupoAvenida.results.metrics.productivity.content"),
		},
		{
			number: t("products.grupoAvenida.results.metrics.cost.number"),
			description: t("products.grupoAvenida.results.metrics.cost.description"),
			content: t("products.grupoAvenida.results.metrics.cost.content"),
		},
		{
			number: t("products.grupoAvenida.results.metrics.results.number"),
			description: t(
				"products.grupoAvenida.results.metrics.results.description",
			),
			content: t("products.grupoAvenida.results.metrics.results.content"),
		},
		{
			number: t("products.grupoAvenida.results.metrics.operations.number"),
			description: t(
				"products.grupoAvenida.results.metrics.operations.description",
			),
			content: t("products.grupoAvenida.results.metrics.operations.content"),
		},
		{
			number: t("products.grupoAvenida.results.metrics.delivery.number"),
			description: t(
				"products.grupoAvenida.results.metrics.delivery.description",
			),
			content: t("products.grupoAvenida.results.metrics.delivery.content"),
		},
	];

	return (
		<section className="bg-[#FFCC32]">
			<div className="container-blog">
				<div className="flex flex-col items-center mb-6 laptop:mb-14">
					<Line className="border-[#2B0C1A]" />

					<Subtitle>
						<h3 className="text-[#2B0C1A]">
							{t("products.grupoAvenida.results.title")}
						</h3>
					</Subtitle>

					<Text className="text-center text-[#2B0C1A] max-w-[342px] laptop:max-w-[920px]">
						{t("products.grupoAvenida.results.description")}
					</Text>
				</div>

				<SwiperWrapper>
					<Swiper
						slidesPerView={1}
						navigation={false}
						spaceBetween={24}
						breakpoints={{
							[size.tablet]: {
								slidesPerView: 2,
							},
							[size.laptopL]: {
								slidesPerView: 4,
							},
						}}
						onSlideChange={(e) => {
							setActiveBullet(e.activeIndex);
						}}
					>
						{results.map((result) => (
							<SwiperSlide key={result.description}>
								<ResultCard result={result} />
							</SwiperSlide>
						))}

						<NavigationContainer className="mt-6">
							<div className="bullets">
								<SlideBulletNavigation activeBullet={activeBullet} />
							</div>
							<div className="arrows">
								<SlideArrowNavigation />
							</div>
						</NavigationContainer>
					</Swiper>
				</SwiperWrapper>
			</div>
		</section>
	);
}
